import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarDays, Check, ChevronLeft, ChevronRight, Clock3, ExternalLink,
  Link2, Save, Settings2, Unplug, Users, Video, XCircle,
} from 'lucide-react';
import { useAuth } from '../../context';
import {
  apiCalendarConnectUrl,
  apiCalendarDisconnect,
  apiCancelConsultation,
  apiGetCalendar,
  apiUpdateCalendarSettings,
} from '../../api';
import AppShell from '../AppShell/AppShell';
import { PageHeader } from '../ui/WorkspaceUI';
import './CalendarPage.css';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const COMMON_TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Toronto', 'Europe/London', 'Europe/Paris', 'Asia/Dubai', 'Asia/Karachi',
  'Asia/Kolkata', 'Asia/Singapore', 'Australia/Sydney', 'UTC',
];

const initialData = { settings: null, connection: { connected: false, email: '', requiredEmail: '' }, bookings: [] };
const keyFor = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const bookingDay = (value, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(value));
  const get = (type) => parts.find((part) => part.type === type)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
};
function Metric({ icon: Icon, label, value, tone = '' }) {
  return <div className={`calendar-metric ${tone}`}><Icon size={18} /><span>{label}</span><strong>{value}</strong></div>;
}

export default function CalendarPage() {
  const { jwtToken } = useAuth();
  const [data, setData] = useState(initialData);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [working, setWorking] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [tab, setTab] = useState('appointments');
  const [month, setMonth] = useState(() => ({ year: new Date().getFullYear(), month: new Date().getMonth() }));
  const [selectedDay, setSelectedDay] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await apiGetCalendar(jwtToken);
      setData(next);
      setDraft(next.settings);
      setError('');
    } catch (nextError) {
      setError(nextError.message || 'Could not load calendar');
    } finally {
      setLoading(false);
    }
  }, [jwtToken]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.get('calendar_success')) { setNotice('Google Calendar connected successfully.'); window.history.replaceState({}, '', '/calendar'); }
    if (query.get('calendar_error')) { setError(query.get('calendar_error')); window.history.replaceState({}, '', '/calendar'); }
  }, []);

  const timeZone = draft?.timezone || 'America/New_York';
  const confirmed = useMemo(() => data.bookings.filter((booking) => booking.status === 'confirmed'), [data.bookings]);
  const upcoming = useMemo(() => confirmed.filter((booking) => new Date(booking.startAt) >= new Date()).sort((a, b) => new Date(a.startAt) - new Date(b.startAt)), [confirmed]);
  const thisWeek = useMemo(() => {
    const end = new Date(Date.now() + 7 * 86400000);
    return upcoming.filter((booking) => new Date(booking.startAt) < end).length;
  }, [upcoming]);
  const dayBookings = useMemo(() => {
    if (selectedDay) return data.bookings.filter((booking) => bookingDay(booking.startAt, timeZone) === selectedDay).sort((a, b) => new Date(a.startAt) - new Date(b.startAt));
    if (showHistory) return [...data.bookings].sort((a, b) => new Date(b.startAt) - new Date(a.startAt));
    return upcoming;
  }, [data.bookings, upcoming, selectedDay, showHistory, timeZone]);

  const cells = useMemo(() => {
    const first = new Date(month.year, month.month, 1).getDay();
    const count = new Date(month.year, month.month + 1, 0).getDate();
    return [...Array(first).fill(null), ...Array.from({ length: count }, (_, index) => index + 1)];
  }, [month]);

  const bookingsByDay = useMemo(() => confirmed.reduce((map, booking) => {
    const key = bookingDay(booking.startAt, timeZone);
    map[key] = (map[key] || 0) + 1;
    return map;
  }, {}), [confirmed, timeZone]);

  const moveMonth = (amount) => {
    const next = new Date(month.year, month.month + amount, 1);
    setMonth({ year: next.getFullYear(), month: next.getMonth() });
    setSelectedDay('');
  };

  const patchDraft = (field, value) => setDraft((current) => ({ ...current, [field]: value }));
  const patchDay = (day, field, value) => setDraft((current) => ({
    ...current,
    weeklyHours: current.weeklyHours.map((item) => item.day === day ? { ...item, [field]: value } : item),
  }));

  const save = async (event) => {
    event.preventDefault();
    setSaving(true); setError(''); setNotice('');
    try {
      const result = await apiUpdateCalendarSettings(jwtToken, draft);
      setDraft(result.settings);
      setData((current) => ({ ...current, settings: result.settings }));
      setNotice('Calendar settings saved.');
    } catch (nextError) { setError(nextError.message); }
    finally { setSaving(false); }
  };

  const connect = async () => {
    setWorking('connect'); setError('');
    try { const result = await apiCalendarConnectUrl(jwtToken); window.location.assign(result.url); }
    catch (nextError) { setError(nextError.message); setWorking(''); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? New times will stop appearing on the public booking page.')) return;
    setWorking('disconnect');
    try { await apiCalendarDisconnect(jwtToken); await load(); setNotice('Google Calendar disconnected.'); }
    catch (nextError) { setError(nextError.message); }
    finally { setWorking(''); }
  };

  const cancelBooking = async (booking) => {
    const reason = window.prompt(`Cancel ${booking.name}'s consultation? Add an optional note for the guest:`, '');
    if (reason === null) return;
    setWorking(booking._id); setError('');
    try { await apiCancelConsultation(jwtToken, booking._id, reason); await load(); setNotice('Booking cancelled and the guest was notified.'); }
    catch (nextError) { setError(nextError.message); }
    finally { setWorking(''); }
  };

  return <AppShell>
    <div className="workspace-page calendar-page">
      <PageHeader title="Calendar" description="Consultation bookings and availability" breadcrumbs={[{ label: 'Workspace', to: '/portal' }, { label: 'Calendar' }]} />

      {error && <div className="calendar-alert error" role="alert"><XCircle size={16} />{error}</div>}
      {notice && <div className="calendar-alert success"><Check size={16} />{notice}</div>}

      <div className="calendar-tabs" role="tablist">
        <button className={tab === 'appointments' ? 'active' : ''} onClick={() => setTab('appointments')}><CalendarDays size={16} />Appointments</button>
        <button className={tab === 'settings' ? 'active' : ''} onClick={() => setTab('settings')}><Settings2 size={16} />Availability & settings</button>
      </div>

      {loading || !draft ? <div className="calendar-loading"><span className="spinner" />Loading calendar…</div> : tab === 'appointments' ? <>
        <div className="calendar-metrics">
          <Metric icon={CalendarDays} label="Upcoming" value={upcoming.length} />
          <Metric icon={Clock3} label="Next 7 days" value={thisWeek} />
          <Metric icon={Users} label="All bookings" value={data.bookings.length} />
          <Metric icon={Link2} label="Google Calendar" value={data.connection.connected ? 'Connected' : 'Action needed'} tone={data.connection.connected ? 'positive' : 'warning'} />
        </div>

        <div className="calendar-workspace-grid">
          <section className="calendar-month-card">
            <header><div><span>Schedule</span><h2>{MONTHS[month.month]} {month.year}</h2></div><div><button onClick={() => moveMonth(-1)} aria-label="Previous month"><ChevronLeft size={17} /></button><button onClick={() => moveMonth(1)} aria-label="Next month"><ChevronRight size={17} /></button></div></header>
            <div className="calendar-week-row">{SHORT_DAYS.map((day) => <span key={day}>{day}</span>)}</div>
            <div className="calendar-month-grid">
              {cells.map((day, index) => {
                if (!day) return <span key={`empty-${index}`} className="calendar-empty-cell" />;
                const key = keyFor(new Date(month.year, month.month, day));
                const count = bookingsByDay[key] || 0;
                const today = key === keyFor(new Date());
                return <button key={key} className={`${selectedDay === key ? 'selected' : ''} ${today ? 'today' : ''}`} onClick={() => setSelectedDay((current) => current === key ? '' : key)}><span>{day}</span>{count > 0 && <small>{count}</small>}</button>;
              })}
            </div>
          </section>

          <section className="calendar-agenda-card">
            <header><div><span>Agenda</span><h2>{selectedDay ? new Date(`${selectedDay}T12:00:00`).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' }) : showHistory ? 'Booking history' : 'Upcoming consultations'}</h2></div><button onClick={() => selectedDay ? setSelectedDay('') : setShowHistory((value) => !value)}>{selectedDay ? 'Clear date' : showHistory ? 'Upcoming' : 'Show history'}</button></header>
            <div className="calendar-agenda-list">
              {dayBookings.length === 0 ? <div className="calendar-empty"><CalendarDays size={30} /><strong>No consultations here</strong><span>{selectedDay ? 'Select another date to view its bookings.' : 'New bookings will appear here automatically.'}</span></div> : dayBookings.map((booking) => <article key={booking._id} className="calendar-booking-row">
                <time><strong>{new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(new Date(booking.startAt))}</strong><span>30 min</span></time>
                <div className="calendar-booking-main"><strong>{booking.name}</strong><span>{booking.email}{booking.phone ? ` · ${booking.phone}` : ''}</span><small className={`booking-status-${booking.status}`}>{booking.status === 'confirmed' ? (booking.service || 'Publishing consultation') : booking.status}</small></div>
                <div className="calendar-booking-actions">{booking.meetUrl && booking.status === 'confirmed' && <a href={booking.meetUrl} target="_blank" rel="noreferrer" title="Open Google Meet"><Video size={16} /></a>}{booking.status === 'confirmed' && new Date(booking.startAt) > new Date() && <button onClick={() => cancelBooking(booking)} disabled={working === booking._id} title="Cancel booking"><XCircle size={16} /></button>}</div>
              </article>)}
            </div>
          </section>
        </div>
      </> : <form onSubmit={save} className="calendar-settings-layout">
        <section className="calendar-settings-card connection-card">
          <header><div><span>Connection</span><h2>Google Calendar & Meet</h2></div><div className={`connection-status ${data.connection.connected ? 'connected' : ''}`}><i />{data.connection.connected ? 'Connected' : 'Not connected'}</div></header>
          <p>Bookings are created on <strong>{data.connection.requiredEmail}</strong>, with a unique Google Meet link and guest invitation.</p>
          {data.connection.connected ? <div className="connected-account"><div><Check size={18} /><span><strong>{data.connection.email}</strong><small>Calendar events and Meet links are active</small></span></div><button type="button" onClick={disconnect} disabled={working === 'disconnect'}><Unplug size={15} />Disconnect</button></div> : <button type="button" className="calendar-connect-button" onClick={connect} disabled={working === 'connect'}><Link2 size={16} />{working === 'connect' ? 'Opening Google…' : 'Connect Google Calendar'}<ExternalLink size={14} /></button>}
        </section>

        <section className="calendar-settings-card">
          <header><div><span>Booking page</span><h2>Consultation details</h2></div><label className="calendar-toggle"><input type="checkbox" checked={draft.enabled} onChange={(event) => patchDraft('enabled', event.target.checked)} /><span />{draft.enabled ? 'Live' : 'Paused'}</label></header>
          <div className="calendar-form-grid">
            <label><span>Public title</span><input value={draft.publicTitle} onChange={(event) => patchDraft('publicTitle', event.target.value)} required /></label>
            <label><span>Host name</span><input value={draft.hostName} onChange={(event) => patchDraft('hostName', event.target.value)} required /></label>
            <label className="wide"><span>Description</span><textarea rows={3} value={draft.publicDescription} onChange={(event) => patchDraft('publicDescription', event.target.value)} /></label>
            <label><span>Calendar event title</span><input value={draft.eventTitle} onChange={(event) => patchDraft('eventTitle', event.target.value)} /><small>Use {'{{name}}'} for the guest’s name.</small></label>
            <label><span>Booking notification email</span><input type="email" value={draft.notificationEmail} onChange={(event) => patchDraft('notificationEmail', event.target.value)} required /></label>
          </div>
        </section>

        <section className="calendar-settings-card">
          <header><div><span>Availability</span><h2>Weekly hours</h2></div><small>All times use {timeZone.replaceAll('_', ' ')}</small></header>
          <div className="weekly-hours">
            {[...draft.weeklyHours].sort((a, b) => a.day - b.day).map((item) => <div key={item.day} className={!item.enabled ? 'disabled' : ''}>
              <label className="day-check"><input type="checkbox" checked={item.enabled} onChange={(event) => patchDay(item.day, 'enabled', event.target.checked)} /><span><Check size={12} /></span>{DAYS[item.day]}</label>
              {item.enabled ? <div><input type="time" value={item.start} onChange={(event) => patchDay(item.day, 'start', event.target.value)} /><em>to</em><input type="time" value={item.end} onChange={(event) => patchDay(item.day, 'end', event.target.value)} /></div> : <small>Unavailable</small>}
            </div>)}
          </div>
        </section>

        <section className="calendar-settings-card">
          <header><div><span>Scheduling rules</span><h2>Limits & buffers</h2></div></header>
          <div className="calendar-form-grid rules-grid">
            <label><span>Business timezone</span><select value={draft.timezone} onChange={(event) => patchDraft('timezone', event.target.value)}>{[draft.timezone, ...COMMON_TIMEZONES.filter((zone) => zone !== draft.timezone)].map((zone) => <option key={zone}>{zone}</option>)}</select></label>
            <label><span>Minimum notice</span><div className="number-suffix"><input type="number" min="0" max="336" value={draft.minimumNoticeHours} onChange={(event) => patchDraft('minimumNoticeHours', Number(event.target.value))} /><i>hours</i></div></label>
            <label><span>Booking window</span><div className="number-suffix"><input type="number" min="1" max="365" value={draft.maximumDaysAhead} onChange={(event) => patchDraft('maximumDaysAhead', Number(event.target.value))} /><i>days</i></div></label>
            <label><span>Buffer before</span><div className="number-suffix"><input type="number" min="0" max="120" step="5" value={draft.bufferBeforeMinutes} onChange={(event) => patchDraft('bufferBeforeMinutes', Number(event.target.value))} /><i>min</i></div></label>
            <label><span>Buffer after</span><div className="number-suffix"><input type="number" min="0" max="120" step="5" value={draft.bufferAfterMinutes} onChange={(event) => patchDraft('bufferAfterMinutes', Number(event.target.value))} /><i>min</i></div></label>
            <label className="wide"><span>Blocked dates</span><input placeholder="2026-12-25, 2026-12-26" value={(draft.blockedDates || []).join(', ')} onChange={(event) => patchDraft('blockedDates', event.target.value.split(',').map((value) => value.trim()).filter(Boolean))} /><small>Comma-separated dates in YYYY-MM-DD format.</small></label>
          </div>
        </section>

        <div className="calendar-save-bar"><span>Appointments are always 30 minutes long.</span><button className="btn btn-primary" type="submit" disabled={saving}><Save size={15} />{saving ? 'Saving…' : 'Save settings'}</button></div>
      </form>}
    </div>
  </AppShell>;
}
